import { useState } from 'react'
import { motion } from 'framer-motion'
import { Zap, Trophy, Sparkles, Loader2, ArrowUpRight } from 'lucide-react'
import { login, sanitizeErrorMessage } from '../api'
import { useStore } from '../store'
import ParticleBackground from './ParticleBackground'
import ExternalLinks from './ExternalLinks'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { setUser, theme } = useStore()

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) { setError('Please enter your email'); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setError('Please enter a valid email'); return }

    setLoading(true)
    setError('')

    try {
      const data = await login(trimmed)
      setUser({ id: data.user_id, email: data.email, balance: data.balance })
    } catch (err: unknown) {
      setError(sanitizeErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 relative"
      style={{ background: 'var(--bg-primary)' }}>

      <ParticleBackground />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
        className="relative w-full max-w-lg z-10"
      >
        {/* Logo — large */}
        <motion.div
          className="flex justify-center mb-10"
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src={theme === 'dark' ? '/LOGO-dark.png' : '/LOGO-light.png'} alt="Perceptrons"
            className="h-[100px] sm:h-[140px] w-auto" />
        </motion.div>

        {/* Card */}
        <div className="rounded-2xl p-8 sm:p-10"
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}>
          <h1 className="text-2xl sm:text-3xl font-bold text-center mb-1"
            style={{ color: 'var(--text-primary)' }}>
            <span className="text-brand">Perceptron</span> Prize Wheel
          </h1>
          <p className="text-center mb-6 text-sm" style={{ color: 'var(--text-muted)' }}>
            Sign in with your dashboard email to spin &amp; win
          </p>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2 mono"
                style={{ color: 'var(--text-muted)' }}>
                Dashboard Email
              </label>
              <input type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={loading} autoFocus
                aria-label="Email address"
                className="w-full px-4 py-3.5 rounded-xl text-sm outline-none transition-all duration-200
                  border-2 disabled:opacity-50"
                style={{
                  background: 'var(--bg-secondary)',
                  borderColor: 'var(--border-subtle)',
                  color: 'var(--text-primary)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--brand-primary)'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-subtle)'
                }}
              />
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs px-4 py-3 rounded-xl border-2"
                style={{
                  color: 'var(--danger)',
                  background: 'rgba(220,38,38,0.05)',
                  borderColor: 'rgba(220,38,38,0.2)',
                }}
              >
                {error}
              </motion.div>
            )}

            <button type="submit" disabled={loading}
              aria-label={loading ? 'Connecting to server' : 'Connect and play'}
              className="w-full py-3.5 rounded-full font-bold text-sm tracking-wide
                btn-brand text-white
                disabled:opacity-50 disabled:cursor-not-allowed
                active:scale-[0.98] transition-all duration-200
                flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  Get Started Now
                  <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Prizes preview */}
          <div className="mt-7 pt-6" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <p className="text-[10px] font-semibold text-center mb-3 uppercase tracking-[0.2em] mono"
              style={{ color: 'var(--text-muted)' }}>
              Win Real Prizes
            </p>
            <div className="flex justify-center gap-2 flex-wrap">
              {[
                { label: 'Free Spin', icon: Sparkles, color: '#16A34A' },
                { label: '5 USDT', icon: Zap, color: '#FF6B00' },
                { label: '100 USDT', icon: Zap, color: '#FF6B00' },
                { label: 'iPhone 17', icon: Trophy, color: '#FF6B00' },
              ].map((p) => (
                <span key={p.label}
                  className="text-[10px] px-3 py-1.5 rounded-full font-semibold border-2 inline-flex items-center gap-1.5"
                  style={{
                    color: p.color,
                    borderColor: p.color + '30',
                    background: p.color + '08',
                  }}>
                  <p.icon className="w-3 h-3" />
                  {p.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* How it works */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-6 grid grid-cols-3 gap-3"
        >
          {[
            { step: '1', label: 'Sign In', desc: 'Use your dashboard email' },
            { step: '2', label: 'Spin', desc: 'Spend points to spin' },
            { step: '3', label: 'Win', desc: 'Claim real prizes' },
          ].map((s) => (
            <div key={s.step} className="text-center rounded-xl p-4"
              style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
              <div className="w-7 h-7 rounded-full mx-auto mb-2 flex items-center justify-center text-[11px] font-black text-white"
                style={{ background: 'var(--brand-primary)' }}>
                {s.step}
              </div>
              <div className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{s.label}</div>
              <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{s.desc}</div>
            </div>
          ))}
        </motion.div>

        {/* Download & Social Links */}
        <div className="mt-6">
          <ExternalLinks variant="full" />
        </div>

        <p className="text-center mt-4 text-xs" style={{ color: 'var(--text-muted)' }}>
          Powered by{' '}
          <a href="https://perceptrons.xyz" target="_blank" rel="noopener noreferrer"
            className="font-semibold hover:underline" style={{ color: 'var(--text-primary)' }}>
            Perceptron Network
          </a>
        </p>
      </motion.div>
    </div>
  )
}
